import * as ReactDOM from "react-dom/client" // Import from "react-dom/client"
import App from "./App"
import { BrowserRouter } from "react-router-dom"
import { Provider } from "mobx-react"
import Utils from "./utils/utils"
import initializeStores from "./stores/storeInitializer"
import appDataService from "@services/appDataService"
import "./index.css"
import "./styles/custom-bootstrap.less"
import "./styles/custom-ant.less"
import "./styles/app.less"
import "./styles/skl-detail.less"
import "./styles/global.less"
import { ErrorBoundary } from "@components/ErrorBoundary"
import BootError from "@components/BootError"
import { ConfigProvider } from "antd"
import dayjs from "dayjs"
import updateLocale from "dayjs/plugin/updateLocale"
import localeData from "dayjs/plugin/localeData"

import { QueryClientProvider } from "@tanstack/react-query"
import { theme } from "@styles/antdTheme"
import { queryClient } from "@lib/queryClient"

// Cấu hình dayjs
dayjs.extend(updateLocale)
dayjs.extend(localeData)
dayjs.updateLocale("en", {
  weekStart: 1, // Thứ 2 = 1, Chủ nhật = 0
})

// 🔥 Chunk Loading Error Handler - Xử lý lỗi khi chunk file không tìm thấy (do deploy mới)
// Khi user đang dùng app và có build mới, các chunk files có thể đã đổi tên
// Handler này sẽ tự động reload trang để load version mới
const setupChunkErrorHandler = () => {
  // Xử lý lỗi khi load script/chunk fail
  window.addEventListener("error", (event) => {
    const errorMessage = event.message || ""
    const errorSource = (event.target as any)?.src || (event.target as any)?.href || ""

    // Kiểm tra nếu là lỗi chunk loading
    const isChunkError =
      errorMessage.includes("chunk") ||
      errorMessage.includes("Loading chunk") ||
      errorMessage.includes("Failed to fetch dynamically imported module") ||
      errorMessage.includes("Loading CSS chunk") ||
      errorSource.includes("static/js/") ||
      errorSource.includes("static/css/")

    if (isChunkError) {
      const reloadKey = "chunk_reload_attempted"
      const lastReload = sessionStorage.getItem(reloadKey)
      const now = Date.now()

      // Chỉ reload nếu chưa reload trong vòng 30 giây (tránh reload loop)
      if (!lastReload || now - Number(lastReload) > 30_000) {
        sessionStorage.setItem(reloadKey, String(now))
        window.location.reload()
      } else {
        console.error("Chunk loading error persists after reload. Skipping auto-reload to prevent loop.", {
          message: errorMessage,
          source: errorSource,
        })
      }
    }
  })

  // Xử lý lỗi khi promise rejection (dynamic import fail)
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason?.message || event.reason?.toString() || ""

    const isChunkLoadError =
      reason.includes("chunk") ||
      reason.includes("Loading chunk") ||
      reason.includes("Failed to fetch dynamically imported module") ||
      reason.includes("ChunkLoadError")

    if (isChunkLoadError) {
      const reloadKey = "chunk_reload_attempted"
      const lastReload = sessionStorage.getItem(reloadKey)
      const now = Date.now()

      if (!lastReload || now - Number(lastReload) > 30_000) {
        sessionStorage.setItem(reloadKey, String(now))
        event.preventDefault()
        window.location.reload()
      } else {
        console.error("Chunk load rejection persists after reload. Skipping to prevent loop.", { reason })
      }
    }
  })
}

// Khởi tạo chunk error handler ngay khi app load
setupChunkErrorHandler()

Utils.setLocalization()

const renderBootError = (error: unknown) => {
  const rootEl = document.getElementById("root")
  if (!rootEl) return

  const message =
    error instanceof Error ? error.message : "Failed to load configuration.json"

  const root = ReactDOM.createRoot(rootEl)
  root.render(<BootError message={message} />)
}

const bootstrapApp = async () => {
  await appDataService.getAppConfiguration()

  const stores = initializeStores()
  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
  )
  root.render(
    <Provider {...stores}>
      <ConfigProvider
        theme={theme}
        form={{
          requiredMark: (label, { required }) => {
            // Chỉ hiển thị dấu * nếu required
            if (!required) return label
            return (
              <>
                {label}
                <span className="ml-1">
                  <span className="skl-color-red">*</span>
                </span>
              </>
            )
          },
        }}
        // Preserve v5 UX: no backdrop blur on overlays
        // Decision: avoid unexpected visual change for existing users
        // Can be enabled later when UX team approves blur effect
        modal={{ mask: { blur: false } }}
        drawer={{ mask: { blur: false } }}
      >
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ErrorBoundary>
              <App />
            </ErrorBoundary>
          </BrowserRouter>
        </QueryClientProvider>
      </ConfigProvider>
    </Provider>
  )

  // registerServiceWorker()
}

bootstrapApp().catch((error) => {
  console.error("App bootstrap failed:", error)
  renderBootError(error)
})
